/**
 * Chunk Load Error Detection and Recovery
 *
 * WP-BUG-52: Detects failed dynamic imports caused by stale builds after
 * deployment (e.g., when Vite-generated chunk hashes change) and forces
 * a hard page refresh. Uses sessionStorage to prevent infinite reload loops.
 *
 * Handles chunk errors from:
 * - manualChunks (vendor, query, state) in vite.config.ts
 * - Future lazy-loaded routes via React.lazy() / dynamic import()
 *
 * Browser-specific error messages:
 * - Chrome/Edge: "Failed to fetch dynamically imported module: ..."
 * - Firefox: "error loading dynamically imported module" or "Loading chunk ... failed"
 * - Safari: "Importing a module script failed"
 * - Vite: "ChunkLoadError" name on the error object
 */

const RELOAD_FLAG_KEY = 'flowio_chunk_reload';

/**
 * Maximum age (in milliseconds) for a reload flag to be considered valid.
 * After this time, the flag is considered stale and will be cleared,
 * allowing a fresh reload attempt. Set to 10 seconds.
 */
const RELOAD_FLAG_MAX_AGE_MS = 10_000;

/**
 * Detects whether an error is a chunk load error from a failed dynamic import.
 *
 * Checks the error name and message against known browser-specific patterns.
 * Does not throw -- returns false for any non-Error or unrecognized input.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  // Vite/Webpack set the error name to "ChunkLoadError"
  if (error.name === 'ChunkLoadError') {
    return true;
  }

  const message = error.message.toLowerCase();

  // Chrome/Edge: "Failed to fetch dynamically imported module: ..."
  if (message.includes('failed to fetch dynamically imported module')) {
    return true;
  }

  // Firefox: "error loading dynamically imported module"
  if (message.includes('error loading dynamically imported module')) {
    return true;
  }

  // Safari: "Importing a module script failed"
  if (message.includes('importing a module script failed')) {
    return true;
  }

  // Generic Vite/Webpack chunk loading failures
  if (message.includes('loading chunk') || message.includes('loading css chunk')) {
    return true;
  }

  return false;
}

/**
 * Handles a chunk load error by attempting a single hard page refresh.
 *
 * On first encounter: sets a sessionStorage flag with a timestamp and
 * triggers `window.location.reload()`. Returns `true` to indicate a
 * reload was initiated.
 *
 * On subsequent encounters (flag already set and not stale): returns
 * `false` to indicate retries are exhausted and the caller should show
 * a user-facing error message instead.
 *
 * @returns `true` if reload was triggered, `false` if retries exhausted.
 */
export function handleChunkLoadError(_error: Error): boolean {
  const existing = sessionStorage.getItem(RELOAD_FLAG_KEY);

  if (existing !== null) {
    const timestamp = parseInt(existing, 10);
    const age = Date.now() - timestamp;

    // If the flag is recent (within max age), retries are exhausted
    if (!isNaN(timestamp) && age < RELOAD_FLAG_MAX_AGE_MS) {
      return false;
    }
    // Flag is stale -- allow a fresh reload attempt
  }

  // First attempt (or stale flag): set flag and reload
  sessionStorage.setItem(RELOAD_FLAG_KEY, Date.now().toString());
  window.location.reload();
  return true;
}

/**
 * Clears the chunk reload flag if it is stale (older than RELOAD_FLAG_MAX_AGE_MS).
 *
 * Should be called once during app initialization (in main.tsx) so that
 * a successful page load after a chunk-error reload clears the flag,
 * allowing future chunk errors to trigger a fresh reload attempt.
 *
 * Preserves recent flags (< 10s old) to avoid clearing mid-reload-cycle.
 */
export function clearChunkReloadFlag(): void {
  const existing = sessionStorage.getItem(RELOAD_FLAG_KEY);

  if (existing === null) {
    return;
  }

  const timestamp = parseInt(existing, 10);

  if (isNaN(timestamp)) {
    // Invalid value -- clean it up
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
    return;
  }

  const age = Date.now() - timestamp;

  if (age >= RELOAD_FLAG_MAX_AGE_MS) {
    // Flag is stale -- page loaded successfully after reload
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
  }
  // Otherwise, flag is recent -- preserve it (reload may still be in progress)
}
